'use client';

import React, { useState } from 'react';
import { StyledMcpHowTo } from './mcpHowTo.styles';
import { CustomLink } from '../link/link.comp';
import { trackEvent } from '@/lib/track';

const SERVER_URL = 'https://mcp.commoninja.com/mcp';
const CLAUDE_COMMAND =
	'claude mcp add --transport http commonninja https://mcp.commoninja.com/mcp';
const CODEX_COMMAND =
	'codex mcp add commonninja --url https://mcp.commoninja.com/mcp';
const DOCS_URL =
	'https://developers.commoninja.com/docs/reference/mcp-server';

interface IMcpStep {
	n: string;
	title: string;
	text: string;
}

const steps: IMcpStep[] = [
	{
		n: '1',
		title: 'Add the server',
		text: 'Drop the MCP URL into Claude, ChatGPT, or Cursor as a custom connector.',
	},
	{
		n: '2',
		title: 'Log in with OAuth',
		text: 'Authorize once in your browser — no API keys, no config files to manage.',
	},
	{
		n: '3',
		title: 'Build by asking',
		text: 'Tell your agent what you want. It creates the widget and embeds it, live.',
	},
];

const CodeLine = ({ value, prompt }: { value: string; prompt?: string }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			trackEvent('MCP feature copy command');
			setTimeout(() => setCopied(false), 2000);
		} catch {
			setCopied(false);
		}
	};

	return (
		<div className="line">
			<code>
				{prompt && <span className="prompt">{prompt}</span>}
				{value}
			</code>
			<button type="button" onClick={handleCopy} aria-label="Copy to clipboard">
				{copied ? 'Copied' : 'Copy'}
			</button>
		</div>
	);
};

export const McpHowTo = () => {
	return (
		<StyledMcpHowTo id="connect">
			<span className="eyebrow">Get started</span>
			<h3>
				Connect your AI in <span className="highlight">under a minute</span>
			</h3>
			<p className="intro">
				No SDK, no API keys. Point your AI client at one URL, authorize once,
				and start building.
			</p>

			<div className="steps">
				{steps.map((s) => (
					<div className="step" key={s.n}>
						<span className="step-num">{s.n}</span>
						<h4>{s.title}</h4>
						<p>{s.text}</p>
					</div>
				))}
			</div>

			<div className="connect">
				<span className="label">Any MCP client — paste this URL</span>
				<CodeLine value={SERVER_URL} />

				<span className="label">Claude Code — one command</span>
				<CodeLine value={CLAUDE_COMMAND} prompt="$ " />

				<span className="label">Codex CLI — one command</span>
				<CodeLine value={CODEX_COMMAND} prompt="$ " />
				<p className="note">
					Then authorize once with <code>codex mcp login commonninja</code>.
				</p>

				<span className="label">ChatGPT — add a connector</span>
				<p className="note">
					In ChatGPT, open <strong>Settings → Apps → Developer mode</strong>,
					create an app, and paste the server URL above as the connector URL.
				</p>
			</div>

			<div className="docs-cta">
				<CustomLink
					variant="gradient"
					href={DOCS_URL}
					target="_blank"
					rel="noopener noreferrer"
					external
					title="Read the full MCP docs"
					onClick={() => trackEvent('MCP feature read docs')}
				>
					Read the Docs
				</CustomLink>
			</div>
		</StyledMcpHowTo>
	);
};
